import { Box, Column, Row } from "@hightouch/htui";

import {
  NAV_CLOSED_BREAKPOINT,
  NAV_OPEN_BREAKPOINT,
} from "src/contexts/nav-width-context";
import { load } from "src/utils/storage";

import { Skeleton } from "src/components/loading";
import { PageContentSkeleton } from "./page-content-skeleton";

/**
 * Storage key for sidebar preference (without the "hightouch-" prefix,
 * which the storage utility adds automatically).
 */
const SIDEBAR_STORAGE_KEY = "sidebar_collapsed";

/**
 * Read the sidebar collapsed preference via the storage utility.
 * This component renders before NavWidthProvider mounts, so we cannot
 * use the useNavWidth hook.
 */
function isSidebarCollapsed(): boolean {
  try {
    return load(SIDEBAR_STORAGE_KEY) === true;
  } catch {
    // Ignore errors
  }
  return false;
}

const NAV_ITEM_TEXT_WIDTHS: Array<{ key: string; width: number }> = [
  { key: "nav-1", width: 110 },
  { key: "nav-2", width: 90 },
  { key: "nav-3", width: 130 },
  { key: "nav-4", width: 80 },
  { key: "nav-5", width: 100 },
];

export const AppShellSkeleton = () => {
  const collapsed = isSidebarCollapsed();
  const navWidth = collapsed ? NAV_CLOSED_BREAKPOINT : NAV_OPEN_BREAKPOINT;

  return (
    <Row height="100vh">
      {/* Sidebar */}
      <Column
        data-testid="shell-skeleton-nav"
        width={navWidth}
        minWidth={navWidth}
        height="100vh"
        position="sticky"
        top={0}
        py={4}
        pl={0.5}
        overflow="hidden"
        bg="base.background"
        zIndex={2}
        gap={4}
      >
        {/* Logo + app name */}
        <Row align="center" gap={2} px={3} height="40px" flexShrink={0}>
          <Skeleton width={28} height={28} borderRadius={6} />
          {!collapsed && <Skeleton width={100} height={16} />}
        </Row>

        {/* Workspace selector */}
        <Box px={2} flexShrink={0}>
          <Skeleton height={36} borderRadius={8} />
        </Box>

        {/* Nav items */}
        <Column gap={1} px={2} flex={1}>
          {NAV_ITEM_TEXT_WIDTHS.map(({ key, width }) => (
            <Row
              key={key}
              align="center"
              gap={2}
              height="32px"
              px={1}
              borderRadius="md"
            >
              <Box flexShrink={0}>
                <Skeleton width={20} height={20} borderRadius={4} />
              </Box>
              {!collapsed && <Skeleton width={width} height={14} />}
            </Row>
          ))}
        </Column>

        {/* Footer: avatar + name */}
        <Row align="center" gap={2} px={3} flexShrink={0}>
          <Skeleton width={32} height={32} circle />
          {!collapsed && <Skeleton width={100} height={14} />}
        </Row>
      </Column>

      {/* Content area */}
      <Column
        data-testid="shell-skeleton-content"
        flex={1}
        minWidth={0}
        pl={0.5}
        pr={2}
        pt={2}
        gap={2}
      >
        <Box
          bg="white"
          borderTopRadius="2xl"
          boxShadow="sm"
          flex={1}
          minHeight={0}
        >
          <PageContentSkeleton />
        </Box>
      </Column>
    </Row>
  );
};
